import {
    onboardingApplicationResponseSchema,
    type AccountDetailsStepInput,
    type CompanyDataStepInput,
    type OnboardingApplicationResponse,
    type RegistrationChannelStepInput,
    type ValidationStepInput,
    type WorkAreaStepInput,
    type WorkCategoriesStepInput,
} from '@houzy/schemas/partner-cockpit/onboarding';
import type { ValidationDocumentType } from '@houzy/schemas/partner-management/partner-validation';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiRequest, retryQueryUnlessClientError, type ApiContractError, type ApiError } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';
import { useTrackEvent } from '../../../lib/trackingEvents';
import type { OnboardingStepId } from '../types';

const onboardingQueryKey = ['onboarding', 'application'] as const;

export const onboardingApplicationQueryOptions = queryOptions({
    queryKey: onboardingQueryKey,
    queryFn: ({ signal }): Promise<OnboardingApplicationResponse> =>
        apiRequest(partnerCockpitApiPath('onboarding'), {
            signal,
            responseSchema: onboardingApplicationResponseSchema,
        }),
    staleTime: STALE_TIME_MS,
    retry: retryQueryUnlessClientError,
});

type SaveOnboardingStepMutation<TInput> = ReturnType<
    typeof useMutation<OnboardingApplicationResponse, ApiError | ApiContractError, TInput>
>;

function useSaveOnboardingStepMutation<TInput>(
    stepPath: string,
    step: OnboardingStepId,
): SaveOnboardingStepMutation<TInput> {
    const queryClient = useQueryClient();
    const track = useTrackEvent();
    return useMutation<OnboardingApplicationResponse, ApiError | ApiContractError, TInput>({
        mutationFn: (body: TInput): Promise<OnboardingApplicationResponse> =>
            apiRequest(partnerCockpitApiPath(`onboarding/${stepPath}`), {
                method: 'PUT',
                body,
                responseSchema: onboardingApplicationResponseSchema,
            }),
        onSuccess: (application: OnboardingApplicationResponse) => {
            queryClient.setQueryData(onboardingQueryKey, application);
            track('Onboarding Step Completed', { step });
        },
    });
}

export function useSaveWorkCategoriesMutation(): SaveOnboardingStepMutation<WorkCategoriesStepInput> {
    return useSaveOnboardingStepMutation<WorkCategoriesStepInput>('work-categories', 'work-categories');
}

export function useSaveWorkAreaMutation(): SaveOnboardingStepMutation<WorkAreaStepInput> {
    return useSaveOnboardingStepMutation<WorkAreaStepInput>('work-area', 'regions');
}

export function useSaveAccountDetailsMutation(): SaveOnboardingStepMutation<AccountDetailsStepInput> {
    return useSaveOnboardingStepMutation<AccountDetailsStepInput>('account', 'account');
}

export function useSaveCompanyDataMutation(): SaveOnboardingStepMutation<CompanyDataStepInput> {
    return useSaveOnboardingStepMutation<CompanyDataStepInput>('company', 'company');
}

export function useSaveValidationMutation(): SaveOnboardingStepMutation<ValidationStepInput> {
    return useSaveOnboardingStepMutation<ValidationStepInput>('validation', 'validation');
}

export function useSaveRegistrationChannelMutation(): SaveOnboardingStepMutation<RegistrationChannelStepInput> {
    return useSaveOnboardingStepMutation<RegistrationChannelStepInput>('registration-channel', 'registration-channel');
}

type ValidationDocumentMutation<TInput> = ReturnType<
    typeof useMutation<OnboardingApplicationResponse, ApiError | ApiContractError, TInput>
>;

export type AddValidationDocumentInput = { type: ValidationDocumentType; file: File };

export function useAddValidationDocumentMutation(): ValidationDocumentMutation<AddValidationDocumentInput> {
    const queryClient = useQueryClient();
    const track = useTrackEvent();
    return useMutation<OnboardingApplicationResponse, ApiError | ApiContractError, AddValidationDocumentInput>({
        mutationFn: ({ type, file }): Promise<OnboardingApplicationResponse> => {
            const body = new FormData();
            body.append('document', file);
            body.append('type', type);
            return apiRequest(partnerCockpitApiPath('onboarding/validation/documents'), {
                method: 'POST',
                body,
                responseSchema: onboardingApplicationResponseSchema,
            });
        },
        onSuccess: (application: OnboardingApplicationResponse) => {
            track('Verification Document Uploaded', {});
            queryClient.setQueryData(onboardingQueryKey, application);
        },
    });
}

export function useDeleteValidationDocumentMutation(): ValidationDocumentMutation<string> {
    const queryClient = useQueryClient();
    return useMutation<OnboardingApplicationResponse, ApiError | ApiContractError, string>({
        mutationFn: (documentUuid: string): Promise<OnboardingApplicationResponse> =>
            apiRequest(partnerCockpitApiPath(`onboarding/validation/documents/${documentUuid}`), {
                method: 'DELETE',
                responseSchema: onboardingApplicationResponseSchema,
            }),
        onSuccess: (application: OnboardingApplicationResponse) => {
            queryClient.setQueryData(onboardingQueryKey, application);
        },
    });
}
